import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

/**
 * Campo de texto das telas de Auth — recipes `.field`/`.input` do `components.css`.
 *
 * Usa as recipes do design system em vez do `Input` de `@powerful/ui` porque o canvas da T1.1
 * desenha o campo como cápsula de 50 px com ícone à esquerda e ação opcional à direita; o `Input`
 * partilhado é um campo retangular sem afixos. As duas formas coexistem por desenho — esta é a
 * do fluxo de Auth.
 *
 * O label é **sempre visível** (§3.5 do plano): placeholder não substitui label, porque desaparece
 * assim que a pessoa começa a escrever e leva consigo a única pista do que o campo pede.
 */
export interface AuthTextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Ícone decorativo à esquerda, dentro da cápsula. */
  icon?: ReactNode;
  /** Ação à direita, dentro da cápsula — usado pelo revelar/ocultar da palavra-passe. */
  action?: ReactNode;
  hint?: string;
  error?: string | null;
  /** Conteúdo extra entre o campo e a dica (ex.: medidor de força). */
  belowField?: ReactNode;
  id?: string;
}

export const AuthTextField = forwardRef<HTMLInputElement, AuthTextFieldProps>(function AuthTextField(
  { label, icon, action, hint, error, belowField, id, className, ...props },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? `${generatedId}-field`;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  // A ordem importa para o leitor de tela: o erro é lido antes da dica genérica, porque é a
  // informação que bloqueia a submissão.
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className="field min-w-0">
      <label htmlFor={fieldId}>{label}</label>
      <div className={`input focus-within:ring-2 focus-within:ring-gold focus-within:ring-offset-2 ${error ? 'is-error' : ''} ${className ?? ''}`}>
        {icon}
        <input
          ref={ref}
          id={fieldId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          // `self-stretch`: sem isto o alvo real do campo é a linha de texto (24 px), não a
          // cápsula de 50 px — e o gate de alvo de toque mede o elemento, não o pai.
          className="min-w-0 flex-1 self-stretch"
          {...props}
        />
        {action}
      </div>
      {belowField}
      {error ? (
        <span id={errorId} role="alert" className="err flex items-center gap-[6px]">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5v5" />
            <path d="M12 16.2h.01" />
          </svg>
          {error}
        </span>
      ) : null}
      {hint ? (
        <span id={hintId} className="hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
});

/** Ícones dos campos de Auth, na espessura de traço do canvas. */
export const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

export const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
    <path d="M8 10.5V7a4 4 0 018 0v3.5" />
  </svg>
);

export const PersonIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M4.5 20c1.2-4 4-6 7.5-6s6.3 2 7.5 6" />
  </svg>
);
